import { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { 
  Users, UserPlus, Mail, Shield, Trash2, MoreVertical, 
  RefreshCw, Send, Clock, CheckCircle, XCircle, AlertTriangle,
  Crown, User, Briefcase, Heart, Phone, Edit2
} from 'lucide-react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../../ui/card';
import { Button } from '../../ui/button';
import { Input } from '../../ui/input';
import { Label } from '../../ui/label';
import { Badge } from '../../ui/badge';
import { Progress } from '../../ui/progress';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '../../ui/dialog';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '../../ui/dropdown-menu';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../../ui/select';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const ROLE_OPTIONS = [
  { value: 'admin', label: 'Administrator', icon: Crown, description: 'Full access to all features' },
  { value: 'manager', label: 'Manager', icon: Briefcase, description: 'Manage team and operations' },
  { value: 'provider', label: 'Provider', icon: Heart, description: 'Medical provider access' },
  { value: 'nurse', label: 'Nurse', icon: Heart, description: 'Nursing staff access' },
  { value: 'receptionist', label: 'Receptionist', icon: Phone, description: 'Front desk access' },
  { value: 'staff', label: 'Staff', icon: User, description: 'Basic staff access' },
];

const getRoleIcon = (role) => {
  const roleOption = ROLE_OPTIONS.find(r => r.value === role);
  return roleOption?.icon || User;
};

const getRoleBadgeColor = (role) => {
  const colors = {
    admin: 'bg-purple-100 text-purple-800',
    manager: 'bg-blue-100 text-blue-800',
    provider: 'bg-green-100 text-green-800',
    nurse: 'bg-teal-100 text-teal-800',
    receptionist: 'bg-orange-100 text-orange-800',
    staff: 'bg-gray-100 text-gray-800',
    clinic_admin: 'bg-purple-100 text-purple-800',
    clinic_owner: 'bg-yellow-100 text-yellow-800',
  };
  return colors[role] || 'bg-gray-100 text-gray-800';
};

const TeamManagement = ({ user }) => {
  const [teamData, setTeamData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [inviteModalOpen, setInviteModalOpen] = useState(false);
  const [editModalOpen, setEditModalOpen] = useState(false);
  const [resetPasswordModalOpen, setResetPasswordModalOpen] = useState(false);
  const [selectedMember, setSelectedMember] = useState(null);
  const [inviteForm, setInviteForm] = useState({
    email: '',
    full_name: '',
    role: 'staff',
    custom_message: ''
  });
  const [resetPasswordForm, setResetPasswordForm] = useState({
    manualPassword: '',
    useManualPassword: false,
    sendEmail: true
  });
  const [submitting, setSubmitting] = useState(false);
  const [limits, setLimits] = useState(null);

  const fetchTeamData = useCallback(async () => {
    try {
      setLoading(true);
      const token = localStorage.getItem('token');
      
      // Fetch both team members and limits in parallel
      const [membersRes, limitsRes] = await Promise.all([
        axios.get(`${API_URL}/api/team/members`, {
          headers: { Authorization: `Bearer ${token}` }
        }),
        axios.get(`${API_URL}/api/team/limits`, {
          headers: { Authorization: `Bearer ${token}` }
        })
      ]);
      
      setTeamData(membersRes.data);
      setLimits(limitsRes.data);
    } catch (error) {
      console.error('Failed to fetch team data:', error);
      toast.error('Failed to load team members');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchTeamData();
  }, [fetchTeamData]);

  const handleInvite = async (e) => {
    e.preventDefault();
    
    if (!inviteForm.email || !inviteForm.full_name) {
      toast.error('Please fill in all required fields');
      return;
    }

    try {
      setSubmitting(true);
      const token = localStorage.getItem('token');
      await axios.post(`${API_URL}/api/team/invite`, inviteForm, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      toast.success(`Invitation sent to ${inviteForm.email}`);
      setInviteModalOpen(false);
      setInviteForm({ email: '', full_name: '', role: 'staff', custom_message: '' });
      fetchTeamData();
    } catch (error) {
      console.error('Failed to send invitation:', error);
      toast.error(error.response?.data?.detail || 'Failed to send invitation');
    } finally {
      setSubmitting(false);
    }
  };

  const handleCancelInvitation = async (invitationId) => {
    if (!window.confirm('Are you sure you want to cancel this invitation?')) return;

    try {
      const token = localStorage.getItem('token');
      await axios.delete(`${API_URL}/api/team/invite/${invitationId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success('Invitation cancelled');
      fetchTeamData();
    } catch (error) {
      toast.error('Failed to cancel invitation');
    }
  };

  const handleResendInvitation = async (invitationId) => {
    try {
      const token = localStorage.getItem('token');
      await axios.post(`${API_URL}/api/team/invite/${invitationId}/resend`, {}, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success('Invitation resent');
    } catch (error) {
      toast.error('Failed to resend invitation');
    }
  };

  const handleUpdateMember = async (memberId, updates) => {
    try {
      const token = localStorage.getItem('token');
      await axios.put(`${API_URL}/api/team/members/${memberId}`, updates, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success('Team member updated');
      setEditModalOpen(false);
      fetchTeamData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to update team member');
    }
  };

  const handleRemoveMember = async (memberId, memberName, permanent = false) => {
    const action = permanent ? 'permanently delete' : 'deactivate';
    const confirmMessage = permanent 
      ? `Are you sure you want to PERMANENTLY DELETE ${memberName}? This will free up their email for reuse. This action cannot be undone.`
      : `Are you sure you want to deactivate ${memberName}? They can be reactivated later.`;
    
    if (!window.confirm(confirmMessage)) return;

    try {
      const token = localStorage.getItem('token');
      const url = permanent 
        ? `${API_URL}/api/team/members/${memberId}?permanent=true`
        : `${API_URL}/api/team/members/${memberId}`;
      
      await axios.delete(url, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      toast.success(permanent 
        ? `${memberName} permanently deleted. Email is now available for reuse.`
        : `${memberName} deactivated`
      );
      fetchTeamData();
    } catch (error) {
      toast.error(error.response?.data?.detail || `Failed to ${action} team member`);
    }
  };

  const openResetPasswordModal = (member) => {
    setSelectedMember(member);
    setResetPasswordForm({
      manualPassword: '',
      useManualPassword: false,
      sendEmail: true
    });
    setResetPasswordModalOpen(true);
  };

  const handleResetPassword = async () => {
    if (!selectedMember) return;
    
    if (resetPasswordForm.useManualPassword && resetPasswordForm.manualPassword.length < 8) {
      toast.error('Password must be at least 8 characters');
      return;
    }

    setSubmitting(true);
    try {
      const token = localStorage.getItem('token');
      const response = await axios.post(
        `${API_URL}/api/auth/admin/reset-password`,
        {
          user_id: selectedMember.user_id || selectedMember.id,
          new_password: resetPasswordForm.useManualPassword ? resetPasswordForm.manualPassword : null,
          send_email: resetPasswordForm.sendEmail
        },
        { headers: { Authorization: `Bearer ${token}` } }
      );

      if (response.data.success) {
        if (response.data.is_temporary && response.data.temporary_password) {
          toast.success(
            <div>
              <p className="font-semibold">Password reset successfully!</p>
              {response.data.email_sent ? (
                <p className="text-sm">Email sent with new credentials</p>
              ) : (
                <div className="mt-2 p-2 bg-gray-100 rounded text-sm">
                  <p className="font-medium">Temporary Password:</p>
                  <code className="text-green-600 font-mono">{response.data.temporary_password}</code>
                </div>
              )}
            </div>,
            { duration: 10000 }
          );
        } else {
          toast.success('Password reset successfully!' + (response.data.email_sent ? ' Email sent.' : ''));
        }
        setResetPasswordModalOpen(false);
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to reset password');
    } finally {
      setSubmitting(false);
    }
  };

  const openEditModal = (member) => {
    setSelectedMember(member);
    setEditModalOpen(true);
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <RefreshCw className="h-8 w-8 animate-spin text-teal-600" />
      </div>
    );
  }

  const staffLimit = limits?.staff_limit ?? teamData?.staff_limit;
  const isUnlimitedStaff = staffLimit === -1 || staffLimit === null || staffLimit === undefined;
  const usagePercentage = isUnlimitedStaff 
    ? 0 
    : limits 
      ? ((limits.current_count || 0) / (limits.staff_limit || 1)) * 100 
      : teamData 
        ? ((teamData.current_count || 0) / (teamData.staff_limit || 1)) * 100 
        : 0;

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h2 className="text-2xl font-bold text-gray-900">Team Management</h2>
          <p className="text-gray-600">Manage your clinic staff and team members</p>
        </div>
        <Button 
          onClick={() => setInviteModalOpen(true)}
          disabled={!teamData?.can_add_more}
          className="bg-teal-600 hover:bg-teal-700"
        >
          <UserPlus className="h-4 w-4 mr-2" />
          Invite Team Member
        </Button>
      </div>

      {/* Staff Limit Card */}
      <Card>
        <CardHeader className="pb-2">
          <div className="flex items-center justify-between">
            <CardTitle className="text-lg flex items-center gap-2">
              <Shield className="h-5 w-5 text-teal-600" />
              Staff Limit
            </CardTitle>
            <div className="flex items-center gap-2">
              <Badge variant="outline" className="capitalize">
                {limits?.subscription_tier || teamData?.subscription_tier || 'Starter'} Plan
              </Badge>
              {(limits?.monthly_price || teamData?.monthly_price) && (
                <Badge variant="secondary" className="text-xs">
                  ${limits?.monthly_price || teamData?.monthly_price}/mo
                </Badge>
              )}
            </div>
          </div>
          <CardDescription className="text-xs text-gray-500">
            Limits are synced with your subscription plan
          </CardDescription>
        </CardHeader>
        <CardContent>
          <div className="space-y-3">
            {(() => {
              const staffLimit = limits?.staff_limit ?? teamData?.staff_limit;
              const currentCount = limits?.current_count || teamData?.current_count || 0;
              const remainingSlots = limits?.remaining_slots ?? teamData?.remaining_slots;
              const isUnlimited = staffLimit === -1 || staffLimit === null || staffLimit === undefined;
              const canAddMore = limits?.can_add_more ?? teamData?.can_add_more ?? true;
              
              return (
                <>
                  <div className="flex items-center justify-between text-sm">
                    <span className="text-gray-600">
                      {currentCount} of {isUnlimited ? '∞' : staffLimit} team members
                    </span>
                    <span className="font-medium">
                      {isUnlimited ? 'Unlimited' : `${Math.max(0, remainingSlots || 0)} slots remaining`}
                    </span>
                  </div>
                  {!isUnlimited && <Progress value={usagePercentage} className="h-2" />}
                  {isUnlimited && (
                    <div className="flex items-center gap-2 text-green-600 text-sm bg-green-50 p-3 rounded-lg">
                      <CheckCircle className="h-4 w-4" />
                      <span>Your plan includes unlimited team members!</span>
                    </div>
                  )}
                  {!canAddMore && !isUnlimited && (
                    <div className="flex items-center gap-2 text-amber-600 text-sm bg-amber-50 p-3 rounded-lg">
                      <AlertTriangle className="h-4 w-4" />
                      <span>Staff limit reached. Upgrade your plan to add more team members.</span>
                    </div>
                  )}
                </>
              );
            })()}
            {(limits?.max_members || teamData?.max_members) && (
              <p className="text-xs text-gray-500">
                Your plan also includes up to {(limits?.max_members || teamData?.max_members) === -1 ? 'unlimited' : (limits?.max_members || teamData?.max_members)} patient members
              </p>
            )}
          </div>
        </CardContent>
      </Card>

      {/* Team Members List */}
      <Card>
        <CardHeader>
          <div className="flex items-center justify-between">
            <div>
              <CardTitle className="flex items-center gap-2">
                <Users className="h-5 w-5" />
                Team Members
              </CardTitle>
              <CardDescription>
                Active team members in your clinic
              </CardDescription>
            </div>
            <Button variant="ghost" size="sm" onClick={fetchTeamData}>
              <RefreshCw className="h-4 w-4" />
            </Button>
          </div>
        </CardHeader>
        <CardContent>
          <div className="space-y-3">
            {teamData?.members?.length === 0 ? (
              <div className="text-center py-8 text-gray-500">
                <Users className="h-12 w-12 mx-auto mb-3 opacity-50" />
                <p>No team members yet</p>
                <p className="text-sm">Invite your first team member to get started</p>
              </div>
            ) : (
              teamData?.members?.map((member) => {
                const RoleIcon = getRoleIcon(member.role);
                const isCurrentUser = member.id === user?.id;
                
                return (
                  <div 
                    key={member.id}
                    className="flex items-center justify-between p-4 bg-gray-50 rounded-lg hover:bg-gray-100 transition-colors"
                  >
                    <div className="flex items-center gap-4">
                      <div className="w-10 h-10 bg-teal-100 rounded-full flex items-center justify-center">
                        <RoleIcon className="h-5 w-5 text-teal-600" />
                      </div>
                      <div>
                        <div className="flex items-center gap-2">
                          <p className="font-medium text-gray-900">
                            {member.full_name || member.email}
                          </p>
                          {isCurrentUser && (
                            <Badge variant="outline" className="text-xs">You</Badge>
                          )}
                        </div>
                        <p className="text-sm text-gray-500">{member.email}</p>
                      </div>
                    </div>
                    <div className="flex items-center gap-3">
                      <Badge className={getRoleBadgeColor(member.role)}>
                        {member.role?.replace('_', ' ')}
                      </Badge>
                      {member.is_active === false && (
                        <Badge variant="destructive">Inactive</Badge>
                      )}
                      {!isCurrentUser && (
                        <DropdownMenu>
                          <DropdownMenuTrigger asChild>
                            <Button variant="ghost" size="sm">
                              <MoreVertical className="h-4 w-4" />
                            </Button>
                          </DropdownMenuTrigger>
                          <DropdownMenuContent align="end">
                            <DropdownMenuItem onClick={() => openEditModal(member)}>
                              <Edit2 className="h-4 w-4 mr-2" />
                              Edit
                            </DropdownMenuItem>
                            <DropdownMenuItem onClick={() => openResetPasswordModal(member)}>
                              <Shield className="h-4 w-4 mr-2" />
                              Reset Password
                            </DropdownMenuItem>
                            <DropdownMenuSeparator />
                            <DropdownMenuItem 
                              className="text-orange-600"
                              onClick={() => handleRemoveMember(member.id, member.full_name, false)}
                            >
                              <XCircle className="h-4 w-4 mr-2" />
                              Deactivate
                            </DropdownMenuItem>
                            <DropdownMenuItem 
                              className="text-red-600"
                              onClick={() => handleRemoveMember(member.id, member.full_name, true)}
                            >
                              <Trash2 className="h-4 w-4 mr-2" />
                              Delete Permanently
                            </DropdownMenuItem>
                          </DropdownMenuContent>
                        </DropdownMenu>
                      )}
                    </div>
                  </div>
                );
              })
            )}
          </div>
        </CardContent>
      </Card>

      {/* Pending Invitations */}
      {teamData?.pending_invitations?.length > 0 && (
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Clock className="h-5 w-5" />
              Pending Invitations
            </CardTitle>
            <CardDescription>
              Invitations waiting to be accepted
            </CardDescription>
          </CardHeader>
          <CardContent>
            <div className="space-y-3">
              {teamData.pending_invitations.map((invitation) => (
                <div 
                  key={invitation.id}
                  className="flex items-center justify-between p-4 bg-amber-50 rounded-lg border border-amber-200"
                >
                  <div className="flex items-center gap-4">
                    <div className="w-10 h-10 bg-amber-100 rounded-full flex items-center justify-center">
                      <Mail className="h-5 w-5 text-amber-600" />
                    </div>
                    <div>
                      <p className="font-medium text-gray-900">{invitation.full_name}</p>
                      <p className="text-sm text-gray-500">{invitation.email}</p>
                    </div>
                  </div>
                  <div className="flex items-center gap-3">
                    <Badge className={getRoleBadgeColor(invitation.role)}>
                      {invitation.role}
                    </Badge>
                    <Badge variant="outline" className="text-amber-600 border-amber-300">
                      Pending
                    </Badge>
                    <DropdownMenu>
                      <DropdownMenuTrigger asChild>
                        <Button variant="ghost" size="sm">
                          <MoreVertical className="h-4 w-4" />
                        </Button>
                      </DropdownMenuTrigger>
                      <DropdownMenuContent align="end">
                        <DropdownMenuItem onClick={() => handleResendInvitation(invitation.id)}>
                          <Send className="h-4 w-4 mr-2" />
                          Resend Invitation
                        </DropdownMenuItem>
                        <DropdownMenuSeparator />
                        <DropdownMenuItem 
                          className="text-red-600"
                          onClick={() => handleCancelInvitation(invitation.id)}
                        >
                          <XCircle className="h-4 w-4 mr-2" />
                          Cancel Invitation
                        </DropdownMenuItem>
                      </DropdownMenuContent>
                    </DropdownMenu>
                  </div>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>
      )}

      {/* Invite Modal */}
      <Dialog open={inviteModalOpen} onOpenChange={setInviteModalOpen}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle>Invite Team Member</DialogTitle>
            <DialogDescription>
              Send an invitation to add a new team member to your clinic.
            </DialogDescription>
          </DialogHeader>
          <form onSubmit={handleInvite}>
            <div className="space-y-4 py-4">
              <div className="space-y-2">
                <Label htmlFor="full_name">Full Name *</Label>
                <Input
                  id="full_name"
                  placeholder="John Doe"
                  value={inviteForm.full_name}
                  onChange={(e) => setInviteForm({ ...inviteForm, full_name: e.target.value })}
                  required
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="email">Email Address *</Label>
                <Input
                  id="email"
                  type="email"
                  placeholder="john@example.com"
                  value={inviteForm.email}
                  onChange={(e) => setInviteForm({ ...inviteForm, email: e.target.value })}
                  required
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="role">Role</Label>
                <Select
                  value={inviteForm.role}
                  onValueChange={(value) => setInviteForm({ ...inviteForm, role: value })}
                >
                  <SelectTrigger>
                    <SelectValue placeholder="Select a role" />
                  </SelectTrigger>
                  <SelectContent>
                    {ROLE_OPTIONS.map((role) => (
                      <SelectItem key={role.value} value={role.value}>
                        <div className="flex items-center gap-2">
                          <role.icon className="h-4 w-4" />
                          <span>{role.label}</span>
                        </div>
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                <p className="text-xs text-gray-500">
                  {ROLE_OPTIONS.find(r => r.value === inviteForm.role)?.description}
                </p>
              </div>
              <div className="space-y-2">
                <Label htmlFor="custom_message">Personal Message (Optional)</Label>
                <Input
                  id="custom_message"
                  placeholder="Welcome to our team!"
                  value={inviteForm.custom_message}
                  onChange={(e) => setInviteForm({ ...inviteForm, custom_message: e.target.value })}
                />
              </div>
            </div>
            <DialogFooter>
              <Button type="button" variant="outline" onClick={() => setInviteModalOpen(false)}>
                Cancel
              </Button>
              <Button type="submit" disabled={submitting} className="bg-teal-600 hover:bg-teal-700">
                {submitting ? (
                  <>
                    <RefreshCw className="h-4 w-4 mr-2 animate-spin" />
                    Sending...
                  </>
                ) : (
                  <>
                    <Send className="h-4 w-4 mr-2" />
                    Send Invitation
                  </>
                )}
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

      {/* Edit Member Modal */}
      <Dialog open={editModalOpen} onOpenChange={setEditModalOpen}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle>Edit Team Member</DialogTitle>
            <DialogDescription>
              Update team member details and role.
            </DialogDescription>
          </DialogHeader>
          {selectedMember && (
            <div className="space-y-4 py-4">
              <div className="space-y-2">
                <Label>Email</Label>
                <Input value={selectedMember.email} disabled className="bg-gray-100" />
              </div>
              <div className="space-y-2">
                <Label htmlFor="edit_name">Full Name</Label>
                <Input
                  id="edit_name"
                  value={selectedMember.full_name || ''}
                  onChange={(e) => setSelectedMember({ ...selectedMember, full_name: e.target.value })}
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="edit_role">Role</Label>
                <Select
                  value={selectedMember.role}
                  onValueChange={(value) => setSelectedMember({ ...selectedMember, role: value })}
                >
                  <SelectTrigger>
                    <SelectValue placeholder="Select a role" />
                  </SelectTrigger>
                  <SelectContent>
                    {ROLE_OPTIONS.map((role) => (
                      <SelectItem key={role.value} value={role.value}>
                        <div className="flex items-center gap-2">
                          <role.icon className="h-4 w-4" />
                          <span>{role.label}</span>
                        </div>
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </div>
          )}
          <DialogFooter>
            <Button type="button" variant="outline" onClick={() => setEditModalOpen(false)}>
              Cancel
            </Button>
            <Button 
              onClick={() => handleUpdateMember(selectedMember.id, {
                full_name: selectedMember.full_name,
                role: selectedMember.role
              })}
              className="bg-teal-600 hover:bg-teal-700"
            >
              <CheckCircle className="h-4 w-4 mr-2" />
              Save Changes
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Reset Password Modal */}
      <Dialog open={resetPasswordModalOpen} onOpenChange={setResetPasswordModalOpen}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Shield className="h-5 w-5 text-orange-600" />
              Reset Password
            </DialogTitle>
            <DialogDescription>
              Reset password for <span className="font-semibold">{selectedMember?.full_name}</span>
              <br />
              <span className="text-xs text-gray-500">{selectedMember?.email}</span>
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-4 py-4">
            {/* Password Type Selection */}
            <div className="space-y-3">
              <Label className="text-sm font-medium">Password Type</Label>
              <div className="flex gap-4">
                <label className="flex items-center gap-2 cursor-pointer">
                  <input
                    type="radio"
                    name="passwordType"
                    checked={!resetPasswordForm.useManualPassword}
                    onChange={() => setResetPasswordForm(prev => ({ ...prev, useManualPassword: false, manualPassword: '' }))}
                    className="w-4 h-4 text-teal-600"
                  />
                  <span className="text-sm">Auto-generate temporary password</span>
                </label>
                <label className="flex items-center gap-2 cursor-pointer">
                  <input
                    type="radio"
                    name="passwordType"
                    checked={resetPasswordForm.useManualPassword}
                    onChange={() => setResetPasswordForm(prev => ({ ...prev, useManualPassword: true }))}
                    className="w-4 h-4 text-teal-600"
                  />
                  <span className="text-sm">Set manually</span>
                </label>
              </div>
            </div>

            {/* Manual Password Input */}
            {resetPasswordForm.useManualPassword && (
              <div className="space-y-2">
                <Label htmlFor="newPassword">New Password</Label>
                <Input
                  id="newPassword"
                  type="password"
                  placeholder="Enter new password (min 8 characters)"
                  value={resetPasswordForm.manualPassword}
                  onChange={(e) => setResetPasswordForm(prev => ({ ...prev, manualPassword: e.target.value }))}
                  data-testid="manual-password-input"
                />
              </div>
            )}

            {/* Send Email Option */}
            <div className="flex items-center gap-2 p-3 bg-blue-50 rounded-lg">
              <input
                type="checkbox"
                id="sendEmail"
                checked={resetPasswordForm.sendEmail}
                onChange={(e) => setResetPasswordForm(prev => ({ ...prev, sendEmail: e.target.checked }))}
                className="w-4 h-4 text-teal-600 rounded"
              />
              <label htmlFor="sendEmail" className="text-sm cursor-pointer">
                <Mail className="h-4 w-4 inline mr-1 text-blue-600" />
                Send email with password reset instructions
              </label>
            </div>

            {/* Warning */}
            <div className="p-3 bg-amber-50 border border-amber-200 rounded-lg text-sm text-amber-800">
              <AlertTriangle className="h-4 w-4 inline mr-1" />
              {resetPasswordForm.useManualPassword 
                ? "The user will need to use this password to login."
                : "A temporary password will be generated. The user will be required to change it on next login."
              }
            </div>
          </div>

          <DialogFooter>
            <Button variant="outline" onClick={() => setResetPasswordModalOpen(false)}>
              Cancel
            </Button>
            <Button 
              onClick={handleResetPassword}
              disabled={submitting || (resetPasswordForm.useManualPassword && resetPasswordForm.manualPassword.length < 8)}
              className="bg-orange-600 hover:bg-orange-700"
              data-testid="confirm-reset-password-btn"
            >
              {submitting ? (
                <>
                  <RefreshCw className="h-4 w-4 mr-2 animate-spin" />
                  Resetting...
                </>
              ) : (
                <>
                  <Shield className="h-4 w-4 mr-2" />
                  Reset Password
                </>
              )}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default TeamManagement;
